/**
 * Micro-interactions and Visual Feedback System
 * Provides enhanced user interactions with smooth animations and feedback
 */

import React, { useState, useRef, useEffect } from "react";
import { cn } from "./utils";

export type InteractionType =
  | "hover"
  | "click"
  | "focus"
  | "tap"
  | "drag"
  | "swipe";

export type AnimationType =
  | "scale"
  | "rotate"
  | "translate"
  | "fade"
  | "bounce"
  | "pulse"
  | "shake"
  | "glow"
  | "lift"
  | "tilt";

interface MicroInteractionProps {
  /** Children to apply interactions to */
  children: React.ReactNode;
  /** Type of interaction to respond to */
  interaction?: InteractionType;
  /** Animation to play on interaction */
  animation?: AnimationType;
  /** Animation duration in ms */
  duration?: number;
  /** Animation delay in ms */
  delay?: number;
  /** Animation easing function */
  easing?: string;
  /** Scale factor for scale animations */
  scale?: number;
  /** Rotation degrees for rotate animations */
  rotation?: number;
  /** Translation distance for translate animations */
  translation?: { x?: number; y?: number };
  /** Custom className */
  className?: string;
  /** Disable interactions */
  disabled?: boolean;
  /** Callback when interaction starts */
  onInteractionStart?: () => void;
  /** Callback when interaction ends */
  onInteractionEnd?: () => void;
}

/**
 * Micro-interaction wrapper component
 */
export const MicroInteraction = React.forwardRef<HTMLDivElement, MicroInteractionProps>(
  ({
    children,
    interaction = "hover",
    animation = "scale",
    duration = 200,
    delay = 0,
    easing = "ease-out",
    scale = 1.05,
    rotation = 5,
    translation = { x: 0, y: -2 },
    className,
    disabled = false,
    onInteractionStart,
    onInteractionEnd,
  }: MicroInteractionProps, ref) => {
  const [isActive, setIsActive] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const getAnimationClasses = () => {
    if (disabled) return "";

    const baseClasses = "transition-all";
    const durationClass = duration <= 200 ? 'duration-fast' : 'duration-normal';

    switch (animation) {
      case "scale":
        return cn(
          baseClasses,
          isActive ? `${durationClass} ease-out` : '',
          isActive ? `scale-105` : "scale-100",
        );

      case "rotate":
        return cn(
          baseClasses,
          isActive ? `${durationClass} ease-out rotate-2` : 'rotate-0',
        );

      case "translate":
        return cn(
          baseClasses,
          isActive ? `${durationClass} ease-out translate-y-1` : 'translate-y-0',
        );

      case "fade":
        return cn(
          baseClasses,
          isActive ? `${durationClass} ease-out opacity-80` : "opacity-100",
        );

      case "bounce":
        return cn(baseClasses, isActive ? "animate-bounce" : "");

      case "pulse":
        return cn(baseClasses, isActive ? "animate-pulse" : "");

      case "shake":
        return cn(baseClasses, isActive ? "animate-pulse" : "");

      case "glow":
        return cn(
          baseClasses,
          isActive ? `${durationClass} shadow-prominent` : "shadow-subtle",
        );

      case "lift":
        return cn(
          baseClasses,
          isActive ? `${durationClass} ease-out -translate-y-1 shadow-elevated` : "translate-y-0 shadow-subtle",
        );

      case "tilt":
        return cn(
          baseClasses,
          isActive ? `${durationClass} ease-out rotate-2 scale-105` : "rotate-0 scale-100",
        );

      default:
        return baseClasses;
    }
  };

  const handleInteractionStart = () => {
    if (disabled) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setTimeout(() => {
      setIsActive(true);
      onInteractionStart?.();
    }, delay);
  };

  const handleInteractionEnd = () => {
    if (disabled) return;

    timeoutRef.current = setTimeout(() => {
      setIsActive(false);
      onInteractionEnd?.();
    }, duration);
  };

  const interactionProps = {
    onMouseEnter: interaction === "hover" ? handleInteractionStart : undefined,
    onMouseLeave: interaction === "hover" ? handleInteractionEnd : undefined,
    onClick: interaction === "click" ? handleInteractionStart : undefined,
    onFocus: interaction === "focus" ? handleInteractionStart : undefined,
    onBlur: interaction === "focus" ? handleInteractionEnd : undefined,
    onTouchStart: interaction === "tap" ? handleInteractionStart : undefined,
    onTouchEnd: interaction === "tap" ? handleInteractionEnd : undefined,
  };

  return (
    <div ref={ref} className={cn(getAnimationClasses(), className)} {...interactionProps}>
      {children}
    </div>
  );
});
MicroInteraction.displayName = 'MicroInteraction';

/**
 * Button with enhanced micro-interactions
 */
export function InteractiveButton({
  children,
  variant = "default",
  size = "default",
  ...props
}: {
  children: React.ReactNode;
  variant?: "default" | "outline" | "ghost" | "link";
  size?: "sm" | "default" | "lg";
} & Omit<MicroInteractionProps, "children">) {
  const baseClasses = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    outline:
      "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    link: "text-primary underline-offset-4 hover:underline",
  };

  const sizeClasses = {
    sm: "h-9 px-3 rounded-md text-sm",
    default: "h-10 px-4 py-2 rounded-md text-sm",
    lg: "h-11 px-8 rounded-md text-base",
  };

  return (
    <MicroInteraction
      animation="lift"
      interaction="hover"
      duration={150}
      {...props}
    >
      <button
        className={cn(
          "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
          baseClasses[variant],
          sizeClasses[size],
        )}
      >
        {children}
      </button>
    </MicroInteraction>
  );
}

/**
 * Card with hover micro-interactions
 */
export function InteractiveCard({
  children,
  className,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
} & MicroInteractionProps) {
  return (
    <MicroInteraction
      animation="lift"
      interaction="hover"
      duration={200}
      className={cn(
        "rounded-lg border bg-card text-card-foreground shadow-sm transition-shadow hover:shadow-md",
        className,
      )}
      {...props}
    >
      <div className="p-6">{children}</div>
    </MicroInteraction>
  );
}

/**
 * Icon with rotation micro-interaction
 */
export function InteractiveIcon({
  children,
  className,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
} & MicroInteractionProps) {
  return (
    <MicroInteraction
      animation="rotate"
      interaction="hover"
      rotation={10}
      duration={200}
      className={className}
      {...props}
    >
      {children}
    </MicroInteraction>
  );
}

/**
 * Hook for managing micro-interaction states
 */
export function useMicroInteractions() {
  const [interactions, setInteractions] = useState<Record<string, boolean>>({});

  const triggerInteraction = (key: string, active: boolean = true) => {
    setInteractions((prev) => ({ ...prev, [key]: active }));
  };

  const isActive = (key: string) => {
    return interactions[key] || false;
  };

  const resetInteractions = () => {
    setInteractions({});
  };

  return {
    interactions,
    triggerInteraction,
    isActive,
    resetInteractions,
  };
}

/**
 * Floating action button with enhanced interactions
 */
export function FloatingActionButton({
  children,
  onClick,
  className,
  ...props
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
} & MicroInteractionProps) {
  return (
    <MicroInteraction
      animation="scale"
      interaction="tap"
      scale={0.95}
      duration={100}
      className={cn(
        "fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all hover:shadow-xl",
        className,
      )}
      onInteractionStart={onClick}
      {...props}
    >
      {children}
    </MicroInteraction>
  );
}

/**
 * Ripple effect component for buttons
 */
export function RippleButton({
  children,
  onClick,
  className,
  ...props
}: {
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
} & Omit<
  MicroInteractionProps,
  "children" | "onInteractionStart" | "onInteractionEnd"
>) {
  const [ripples, setRipples] = useState<
    Array<{ id: number; x: number; y: number }>
  >([]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const newRipple = {
      id: Date.now(),
      x,
      y,
    };

    setRipples((prev) => [...prev, newRipple]);

    // Remove ripple after animation
    setTimeout(() => {
      setRipples((prev) => prev.filter((ripple) => ripple.id !== newRipple.id));
    }, 600);

    onClick?.(event);
  };

  return (
    <button
      className={cn(
        "relative overflow-hidden rounded-md bg-primary px-4 py-2 text-primary-foreground transition-all hover:bg-primary/90",
        className,
      )}
      onClick={handleClick}
      {...props}
    >
      {children}
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="absolute animate-ping rounded-full bg-white/30"
          style={{
            left: ripple.x - 10,
            top: ripple.y - 10,
            width: 20,
            height: 20,
            animationDuration: "0.6s",
          }}
        />
      ))}
    </button>
  );
}
